import React from 'react';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import { useLocation } from 'react-router-dom';

import { Flex, FlexColumn } from 'src/components/flex';
import Responsive from 'src/components/responsive/Responsive';
import DesktopNavigationSidebar from 'src/components/layout/landlord/DesktopNavigationSidebar';
import FacebookIntegrationAlert from 'src/components/layout/landlord/FacebookIntegrationAlert';
import MaintenanceWindowAlert from 'src/components/layout/landlord/MaintenanceWindowAlert';
import HelpSidebar from 'src/components/layout/landlord/help-sidebar/HelpSidebar';
import LandlordLayoutProvider, { useLandlordLayoutContext } from 'src/components/layout/landlord/LandlordLayoutProvider';
import MobileAppBar from 'src/components/layout/landlord/MobileAppBar';
import MobileNavigationDrawer from 'src/components/layout/landlord/MobileNavigationDrawer';
import UnmatchedListingAlert from 'src/components/layout/landlord/UnmatchedListingAlert';
import SentryErrorBoundary from 'src/components/sentry-error-boundary';
import VacantPropertiesAlert from 'src/components/layout/landlord/VacantPropertiesAlert';
import NotificationAlert from 'src/pages/landlord/conversations/components/NotificationAlert';

type Props = {
    isLoading?: boolean;
    children?: React.ReactNode;
}

function LandlordLayoutContent({ isLoading, children }: Props) {
    const { isDrawerOpen, setDrawerOpen } = useLandlordLayoutContext();
    const { pathname } = useLocation();

    const handleDrawerOpen = () => {
        setDrawerOpen(true);
    };

    const handleDrawerClose = () => {
        setDrawerOpen(false);
    };

    const hideUnmatchedAndVacantAlerts =
        pathname === '/landlord/inner/unmatched-listings' ||
        pathname.startsWith('/landlord/inner/messages/from-lead') ||
        (pathname.startsWith('/landlord/inner/playbooks') && pathname !== '/landlord/inner/playbooks');

    return (
        <>
            <FlexColumn minHeight="100vh" rowGap={0} columnGap={0}>
                <Responsive mobile>
                    <div>
                        <MobileAppBar openNavigationDrawer={handleDrawerOpen} />
                    </div>
                </Responsive>

                <Flex
                    flex={1}
                    minHeight="100%"
                    bgcolor="var(--background-color-beige)"
                    columnGap={0}
                    overflow="hidden"
                >
                    <DesktopNavigationSidebar onNavigationChange={handleDrawerClose} />

                    <SentryErrorBoundary>
                        <FlexColumn position="relative" sx={{ flex: 1, overflow: 'hidden' }}>
                            {isLoading && (
                                <Box
                                    position="absolute"
                                    top={0}
                                    left={0}
                                    width="100%"
                                >
                                    <LinearProgress />
                                </Box>
                            )}
                            {!isLoading && (
                                <>
                                    <FacebookIntegrationAlert />
                                    {!hideUnmatchedAndVacantAlerts && <UnmatchedListingAlert />}
                                    <MaintenanceWindowAlert />
                                    {children}
                                </>
                            )}
                        </FlexColumn>
                    </SentryErrorBoundary>
                </Flex>

                {isDrawerOpen && (
                    <Responsive mobile>
                        <div>
                            <MobileNavigationDrawer closeNavigationDrawer={handleDrawerClose} />
                        </div>
                    </Responsive>
                )}
            </FlexColumn>

            {!isLoading && (
                <>
                    <HelpSidebar />
                    <Box
                        sx={{
                            position: 'fixed',
                            bottom: 24,
                            right: 24,
                            zIndex: 1300,
                            display: { xs: 'none', sm: 'flex' },
                            flexDirection: 'column',
                            gap: 1.5,
                        }}
                    >
                        {!hideUnmatchedAndVacantAlerts && <VacantPropertiesAlert />}
                        <NotificationAlert />
                    </Box>
                </>
            )}
        </>
    );
}

export default function LandlordLayout({ isLoading, children }: Props) {
    return (
        <LandlordLayoutProvider>
            <LandlordLayoutContent isLoading={isLoading}>
                {children}
            </LandlordLayoutContent>
        </LandlordLayoutProvider>
    );
}
